import OptionButton from '@atoms/OptionButton';
import {useEffect, useMemo, useState} from 'react';
import SelectDateInput from '@components/molecules/SelectDateInput';
import {useLocation} from 'react-router';
import {
  formatTodayFull,
  formatNonTodayFull,
  toISODateString,
  parseISODateStrict,
} from '@utils/common';
import {useSystemHistory} from '@hooks/useSystemHistory';

export const SystemHistory = () => {
  const location = useLocation();
  const {handleCompare, handleShowSingle, handleShowPlan, plannedDates} =
    useSystemHistory();

  const [selectedRange, setSelectedRange] = useState<[Date, Date] | null>(null);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const hasPlanToday = useMemo(() => {
    const todayISO = toISODateString(today);
    return plannedDates.includes(todayISO);
  }, [plannedDates, today]);

  const isSingleSelection =
    !!selectedRange && selectedRange[0].getTime() === selectedRange[1].getTime();
  const selectedIsToday = useMemo(() => {
    if (!selectedRange) return true;
    if (!isSingleSelection) return false;
    return selectedRange[0].getTime() === today.getTime();
  }, [selectedRange, isSingleSelection, today]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const leftStr = params.get('left');
    const rightStr = params.get('right');

    if (!leftStr || !rightStr) {
      setSelectedRange(null);
      return;
    }

    const leftDate = leftStr ? parseISODateStrict(leftStr) : null;
    const rightDate = rightStr ? parseISODateStrict(rightStr) : null;
    if (leftDate && rightDate) {
      setSelectedRange([leftDate, rightDate]);
    } else {
      setSelectedRange(null);
    }
  }, [location.search]);

  const optionTitle = useMemo(() => {
    if (!selectedRange) {
      return formatTodayFull(today);
    }
    const [a, b] = selectedRange;
    if (a.getTime() === b.getTime()) {
      const isToday = a.getTime() === today.getTime();
      return isToday ? formatTodayFull(today) : formatNonTodayFull(a);
    }
    const left = a < b ? a : b;
    const right = a > b ? a : b;
    return `${formatNonTodayFull(left)} / ${formatNonTodayFull(right)}`;
  }, [selectedRange, today]);

  return (
    <OptionButton
      value={'1'}
      type="calendar"
      options={[]}
      buttonTitle={optionTitle}
      dropdownAlign="right"
      plannedToday={hasPlanToday && selectedIsToday}
      iconVariant={'date'}
      dropdownContent={
        <SelectDateInput
          onRangeChange={setSelectedRange}
          onCompare={handleCompare}
          onShowSingle={handleShowSingle}
          onShowPlan={handleShowPlan}
        />
      }
    />
  );
};
